import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaError, setTwoFaError] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.detail ?? 'Login failed.'); return; }
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setNeeds2FA(true);
      } else {
        nav('/profile');
      }
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }

  async function handle2FA(e: React.FormEvent) {
    e.preventDefault();
    setTwoFaError('');
    setTwoFaLoading(true);
    try {
      const r = await fetch('/api/v1/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_token: tempToken, code: twoFaCode }),
      });
      const data = await r.json();
      if (!r.ok) { setTwoFaError(data.detail ?? 'Invalid 2FA code.'); return; }
      nav('/profile');
    } catch {
      setTwoFaError('Network error.');
    } finally {
      setTwoFaLoading(false);
    }
  }

  if (needs2FA) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
          <h1 className="mb-1 text-2xl font-bold text-slate-900">Two-factor authentication</h1>
          <p className="mb-6 text-sm text-slate-500">Enter the 6-digit code from your authenticator app.</p>
          <form onSubmit={handle2FA} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={twoFaCode}
              onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />
            {twoFaError && <p className="text-sm text-rose-600">{twoFaError}</p>}
            <button
              type="submit"
              disabled={twoFaLoading || twoFaCode.length < 6}
              className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white transition hover:bg-sky-700 disabled:opacity-50"
            >
              {twoFaLoading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
        <Link to="/" className="mb-6 block text-xs text-slate-400 hover:text-slate-600">← Back to picks</Link>
        <h1 className="mb-1 text-2xl font-bold text-slate-900">Sign in</h1>
        <p className="mb-6 text-sm text-slate-500">Access your pick history and premium strategies.</p>
        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
            autoComplete="email"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
          />
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="text-center text-xs">
            <Link to="/forgot-password" className="text-slate-400 hover:text-slate-600">Forgot password?</Link>
          </p>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          No account?{' '}
          <Link to="/register" className="font-semibold text-sky-600 hover:text-sky-800">Register free</Link>
        </p>
      </div>
    </div>
  );
}
